import LoadingBar from '@cloudscape-design/chat-components/loading-bar';
import {
  Box,
  Button,
  Container,
  ContentLayout,
  DatePicker,
  FormField,
  Header,
  Input,
  LiveRegion,
  SegmentedControl,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import { Timestamp } from '@firebase/firestore';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalStorage } from 'react-use';
import { createItem, Filter, updateItem } from '../api/generic';
import { fetchUrlFromPath, uploadFileToStorage } from '../api/storage';
import GridLinkModal from '../components/modules/GridLinkModal';
import { useAlertContext } from '../contexts/AlertContext';
import { useAuthContext } from '../contexts/AuthContext';
import useSubscribe from '../hooks/useSubscribe';
import { parsePdfToText } from '../lib/invoiceParser';
import { Invoice } from '../models/Invoice';
import { formatQuarterHeader, getEndOfQuarter, getStartOfQuarter, isValidFirestoreDate, toMillis } from '../utils/date';
import { prefixCollection } from '../utils/prefix';
import { toDate } from '../utils/timestamp';

export type InvoiceViewModel = {
  id: string;
  supplierName: string;
  invoiceNumber: string;
  invoiceDate: string; // formatted e.g. "Mar 25, 2025"
  uploadedAt: string; // formatted
  currency: string;
  totalAmount: string; // e.g., "€250.00"
  itemCount: number;
  status: 'Parsed' | 'Pending' | 'Error';
  fileUrl?: string;
  items?: InvoiceViewModel[];
};

function getQuarter(date: Date): number {
  return Math.floor(date.getMonth() / 3) + 1;
}

const Invoices = () => {
  const alert = useAlertContext();
  const { tenant, userObject } = useAuthContext();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [processing, setProcessing] = useState<boolean>(false);
  const [showModal, setShowModal] = useState<boolean>(false);

  const [selectedInvoice, setSelectedInvoice] = useState<string>();

  const [invoiceToAdd, setInvoiceToAdd] = useState<Invoice>();
  const [invoiceToAddFile, setInvoiceToAddFile] = useState<File>();

  // All invoice rows (parents + currently-expanded children)
  const [invoices, setInvoices] = useState<InvoiceViewModel[]>([]);

  // Tracks which parent invoice rows are expanded (by ID)
  const [expandedInvoiceIds, setExpandedInvoiceIds] = useState<Set<string>>(new Set());

  const [editingInvoiceId, setEditingInvoiceId] = useState<string>();
  const [showAll, setShowAll] = useState(false);

  const [storeDate, setStoreDate] = useLocalStorage<Date>('activeQuarterDate', new Date());
  // (Optional) Raw fetched data from Firestore before flattening
  // Only needed if you're toggling expansion dynamically at runtime
  const [fullInvoices, setFullInvoices] = useState<Invoice[]>([]);
  const [rawInvoices, setRawInvoices] = useState<InvoiceViewModel[]>([]);
  const [activeQuarterDate, setActiveQuarterDate] = useState<Date>(new Date(storeDate?.toString() ?? ''));

  useEffect(() => {
    setStoreDate(activeQuarterDate);
  }, [activeQuarterDate]);

  const startDate = Timestamp.fromDate(getStartOfQuarter(activeQuarterDate));
  const endDate = Timestamp.fromDate(getEndOfQuarter(activeQuarterDate));

  const invoiceFilters = [
    { field: 'invoiceDate', operator: '>=', value: startDate },
    { field: 'invoiceDate', operator: '<=', value: endDate },
  ] satisfies Filter<Invoice>[];

  function toInvoiceViewModel(invoice: Invoice): InvoiceViewModel {
    return {
      id: invoice.id,
      supplierName: invoice.supplierName,
      invoiceNumber: invoice.invoiceNumber,
      invoiceDate: isValidFirestoreDate(invoice.invoiceDate)
        ? toDate(invoice.invoiceDate).toLocaleDateString()
        : '⚠️ Invalid date',
      uploadedAt: invoice.uploadedAt ? toDate(invoice.uploadedAt).toLocaleDateString() : '',
      currency: invoice.currency,
      totalAmount: `${invoice.totalAmountInclVat}`,
      itemCount: invoice.items.length,
      status: invoice.status === 'parsed' ? 'Parsed' : 'Pending',
      fileUrl: invoice.fileUrl,
      items: invoice.items.map(
        (item) =>
          ({
            id: item.id.toString(),
            supplierName: `${item.quantity} ${item.unit} ${item.description}`,
            totalAmount: `${item.total}`,
            currency: invoice.currency,
          }) as InvoiceViewModel,
      ),
    };
  }

  useSubscribe<Invoice>(
    prefixCollection('invoices', tenant),
    (invoices) => {
      const viewModels = invoices.map(toInvoiceViewModel);
      setRawInvoices(viewModels);
      setInvoices(viewModels);
      setFullInvoices(invoices);
    },
    (error) => console.error('Failed to subscribe to invoices:', error),
    (a, b) => toMillis(b.invoiceDate) - toMillis(a.invoiceDate),
    showAll ? [] : invoiceFilters,
  );

  const handleOpenInvoice = async (invoice: InvoiceViewModel) => {
    if (!invoice.fileUrl) return;
    const url = await fetchUrlFromPath(invoice.fileUrl);
    window.open(url, '_blank');
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    setEditingInvoiceId(undefined);
    setInvoiceToAdd(undefined);

    if (!file) return;

    let text = '';

    try {
      if (file.type === 'application/pdf') {
        setProcessing(true);
        text = await parsePdfToText(file);
      } else {
        alert.warn('Only PDF text extraction is supported in this demo.');
      }
    } catch (error) {
      console.error('Failed to extract text from file:', error);
    }

    // Exit early if no text extracted
    if (!text) {
      setProcessing(false);
      alert.warn('No text extracted from the file.');
      return;
    }

    const response = await fetch('https://parseinvoicefunction-ogbfqlrosa-ew.a.run.app', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text, filename: file.name, debug: true }),
    });

    const data = await response.json();
    setInvoiceToAdd({
      ...data,
      uploadedBy: userObject?.uid || '',
      uploadedAt: new Date(),
      status: 'parsed',
      filename: file.name,
    });
    setInvoiceToAddFile(file);
    setProcessing(false);
    event.target.value = '';
    inputRef.current?.value && (inputRef.current.value = '');
  };

  const handleSaveEditInvoice = async () => {
    if (!invoiceToAdd || !editingInvoiceId) return;

    const updatedInvoice: Invoice = {
      ...invoiceToAdd,
      invoiceDate: new Date(
        isValidFirestoreDate(invoiceToAdd.invoiceDate)
          ? toDate(invoiceToAdd.invoiceDate).toISOString()
          : invoiceToAdd.invoiceDate.toString(),
      ),
      uploadedAt: new Date(),
    };

    await updateItem(prefixCollection('invoices', tenant), editingInvoiceId, updatedInvoice).then(() => {
      alert.success('Invoice updated successfully!');
      setEditingInvoiceId(undefined);
      setInvoiceToAdd(undefined);
    });
  };

  const handleSaveInvoice = async () => {
    if (!invoiceToAdd || !invoiceToAddFile || editingInvoiceId) return;

    const iDate = new Date(invoiceToAdd.invoiceDate.toString());
    const filePath = `${tenant}/invoices/${iDate.getFullYear()}-Q${getQuarter(iDate)}`;
    const fileName = `${invoiceToAdd.supplierName}-${invoiceToAdd.invoiceNumber}.${invoiceToAddFile.name.split('.').pop()}`;

    await uploadFileToStorage(
      invoiceToAddFile,
      fileName,
      filePath,
      () => {},
      () => alert.error('Error uploading file'),
      () => {
        const invoice: Invoice = {
          ...invoiceToAdd,
          invoiceDate: new Date(invoiceToAdd.invoiceDate.toString()),
          uploadedAt: new Date(),
          fileUrl: filePath + '/' + fileName,
        };
        setActiveQuarterDate(toDate(invoice.invoiceDate));
        createItem(prefixCollection('invoices', tenant), invoice).then(() => {
          alert.success('Invoice saved successfully!');
          setInvoiceToAdd(undefined);
          setInvoiceToAddFile(undefined);
        });
      },
    );
  };

  const handleEditInvoice = (invoiceId: string) => {
    if (!invoiceId) return;

    const invoice = fullInvoices.find((i) => i.id === invoiceId);
    if (!invoice) return;

    setEditingInvoiceId(invoiceId);
    setInvoiceToAdd(invoice as Invoice);
  };

  return (
    <>
      <input
        ref={inputRef}
        id="file-picker"
        type="file"
        accept=".pdf,.jpg,.jpeg,.png"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />
      <ContentLayout
        disableOverlap
        header={
          <Header
            variant="h1"
            description={
              'Hint: All ingredients in a batch must be linked to an invoice (multiple invoices can be linked to a batch)'
            }
            actions={
              <SpaceBetween size="m" direction="horizontal">
                <Button
                  disabled={processing}
                  variant="primary"
                  iconName={processing ? 'status-pending' : 'add-plus'}
                  onClick={() => inputRef.current?.click()}
                >
                  Add Invoice
                </Button>
              </SpaceBetween>
            }
          >
            Invoices
          </Header>
        }
      >
        <SpaceBetween size="m" direction="vertical">
          {processing && (
            <LiveRegion>
              <Box margin={{ bottom: 'xs', left: 'l' }} color="text-body-secondary">
                Talking to geepetee
              </Box>
              <LoadingBar variant="gen-ai" />
            </LiveRegion>
          )}
          {invoiceToAdd && (
            <Container
              header={<Header>Invoice Details</Header>}
              footer={
                <SpaceBetween size="m" direction="horizontal">
                  <Button
                    variant="normal"
                    iconName="close"
                    onClick={() => {
                      setEditingInvoiceId(undefined);
                      setInvoiceToAdd(undefined);
                    }}
                  >
                    Clear
                  </Button>
                  <Button
                    variant="primary"
                    iconName="check"
                    onClick={editingInvoiceId ? handleSaveEditInvoice : handleSaveInvoice}
                  >
                    Save
                  </Button>
                </SpaceBetween>
              }
            >
              <SpaceBetween size="m" direction="vertical">
                <FormField label="Supplier Name">
                  <Input
                    value={invoiceToAdd.supplierName}
                    onChange={({ detail }) => setInvoiceToAdd({ ...invoiceToAdd, supplierName: detail.value })}
                    placeholder="Supplier name"
                  />
                </FormField>

                <FormField label="Invoice Number">
                  <Input
                    value={invoiceToAdd.invoiceNumber}
                    onChange={({ detail }) => setInvoiceToAdd({ ...invoiceToAdd, invoiceNumber: detail.value })}
                    placeholder="Invoice number"
                  />
                </FormField>

                <FormField label="Invoice Date">
                  <DatePicker
                    value={
                      isValidFirestoreDate(invoiceToAdd.invoiceDate)
                        ? toDate(invoiceToAdd.invoiceDate).toISOString()
                        : invoiceToAdd.invoiceDate.toString()
                    }
                    onChange={({ detail }) => setInvoiceToAdd({ ...invoiceToAdd, invoiceDate: new Date(detail.value) })}
                  />
                </FormField>

                <FormField label="Currency">
                  <Input
                    value={invoiceToAdd.currency}
                    onChange={({ detail }) => setInvoiceToAdd({ ...invoiceToAdd, currency: detail.value })}
                    placeholder="Currency (e.g. EUR)"
                  />
                </FormField>

                <FormField label="Total Amount Incl. VAT">
                  <Input
                    value={invoiceToAdd.totalAmountInclVat.toString()}
                    type="number"
                    onChange={({ detail }) =>
                      setInvoiceToAdd({ ...invoiceToAdd, totalAmountInclVat: parseFloat(detail.value) || 0 })
                    }
                  />
                </FormField>

                <FormField label="VAT Amount">
                  <Input
                    value={invoiceToAdd.vatAmount.toString()}
                    type="number"
                    onChange={({ detail }) =>
                      setInvoiceToAdd({ ...invoiceToAdd, vatAmount: parseFloat(detail.value) || 0 })
                    }
                  />
                </FormField>

                <FormField label="Total Amount Excl. VAT">
                  <Input
                    value={invoiceToAdd.totalAmountExclVat.toString()}
                    type="number"
                    onChange={({ detail }) =>
                      setInvoiceToAdd({ ...invoiceToAdd, totalAmountExclVat: parseFloat(detail.value) || 0 })
                    }
                  />
                </FormField>

                <Header variant="h3">Invoice Line Items</Header>
                <SpaceBetween size="m" direction="vertical">
                  {invoiceToAdd.items.map((item, index) => (
                    <Container key={item.id}>
                      <SpaceBetween size="s" direction="vertical">
                        <FormField label="Description">
                          <Input
                            value={item.description}
                            onChange={({ detail }) => {
                              const updatedItems = [...invoiceToAdd.items];
                              updatedItems[index] = { ...item, description: detail.value };
                              setInvoiceToAdd({ ...invoiceToAdd, items: updatedItems });
                            }}
                          />
                        </FormField>

                        <SpaceBetween size="s" direction="horizontal">
                          <FormField label="Quantity">
                            <Input
                              value={item.quantity.toString()}
                              type="number"
                              onChange={({ detail }) => {
                                const updatedItems = [...invoiceToAdd.items];
                                updatedItems[index] = { ...item, quantity: parseFloat(detail.value) || 0 };
                                setInvoiceToAdd({ ...invoiceToAdd, items: updatedItems });
                              }}
                            />
                          </FormField>

                          <FormField label="Unit">
                            <Input
                              value={item.unit}
                              onChange={({ detail }) => {
                                const updatedItems = [...invoiceToAdd.items];
                                updatedItems[index] = { ...item, unit: detail.value };
                                setInvoiceToAdd({ ...invoiceToAdd, items: updatedItems });
                              }}
                            />
                          </FormField>

                          <FormField label="Unit Price">
                            <Input
                              value={item.unitPrice.toString()}
                              type="number"
                              onChange={({ detail }) => {
                                const updatedItems = [...invoiceToAdd.items];
                                updatedItems[index] = { ...item, unitPrice: parseFloat(detail.value) || 0 };
                                setInvoiceToAdd({ ...invoiceToAdd, items: updatedItems });
                              }}
                            />
                          </FormField>

                          <FormField label="Total">
                            <Input
                              value={item.total.toString()}
                              type="number"
                              onChange={({ detail }) => {
                                const updatedItems = [...invoiceToAdd.items];
                                updatedItems[index] = { ...item, total: parseFloat(detail.value) || 0 };
                                setInvoiceToAdd({ ...invoiceToAdd, items: updatedItems });
                              }}
                            />
                          </FormField>
                        </SpaceBetween>
                      </SpaceBetween>
                    </Container>
                  ))}
                </SpaceBetween>
              </SpaceBetween>
            </Container>
          )}

          {invoices && (
            <Table
              header={
                <Header variant="h2" actions={undefined}>
                  <SegmentedControl
                    selectedId="seg-2"
                    onChange={({ detail }) => {
                      if (detail.selectedId === 'now') {
                        setActiveQuarterDate(new Date());
                        return;
                      }
                      const direction = detail.selectedId;
                      setActiveQuarterDate((prev) => {
                        const currentMonth = prev.getMonth();
                        const newMonth =
                          direction === 'prev'
                            ? currentMonth - 3
                            : direction === 'next'
                              ? currentMonth + 3
                              : currentMonth;

                        return new Date(prev.getFullYear(), newMonth, 1);
                      });
                    }}
                    label="Quarter navigation"
                    options={[
                      { text: '<', id: 'prev' },
                      { text: `${formatQuarterHeader(getStartOfQuarter(activeQuarterDate))}`, id: 'seg-2' },
                      { text: 'Now', id: 'now' },
                      { text: '>', id: 'next' },
                    ]}
                  />
                </Header>
              }
              trackBy="id"
              items={invoices}
              columnDefinitions={[
                { header: 'Supplier', id: 'supplierName', cell: (item) => item.supplierName },
                { header: 'Invoice', id: 'invoiceNumber', cell: (item) => item.invoiceNumber },
                { header: 'Invoice Date', id: 'invoiceDate', cell: (item) => item.invoiceDate },
                { header: 'Uploaded', id: 'uploadedAt', cell: (item) => item.uploadedAt },

                {
                  header: 'Total',
                  id: 'totalAmount',
                  cell: (item) => `${item.currency} ${parseFloat(item.totalAmount).toFixed(2)}`,
                },
                { header: 'Items', id: 'itemCount', cell: (item) => item.itemCount },
                { header: 'Status', id: 'status', cell: (item) => item.status },
                {
                  header: 'Actions',
                  id: 'actions',
                  cell: (item) =>
                    item.fileUrl && (
                      <SpaceBetween size="s" direction="horizontal">
                        <Button
                          variant="inline-icon"
                          iconName="caret-right-filled"
                          onClick={() => navigate(`/invoices/${item.id}`)}
                        />
                        <Button variant="inline-icon" iconName="edit" onClick={() => handleEditInvoice(item.id)} />
                        <Button variant="inline-icon" iconName="external" onClick={() => handleOpenInvoice(item)} />
                        <Button
                          variant="inline-icon"
                          iconName="grid-view"
                          onClick={() => {
                            setSelectedInvoice(item.id);
                            setShowModal(true);
                          }}
                        />
                      </SpaceBetween>
                    ),
                },
              ]}
              expandableRows={{
                isItemExpandable: (item) => !!item.items?.length,
                getItemChildren: (item) =>
                  item.items?.map((child) => ({ ...child, id: `${item.id}-${child.id}` })) ?? [],
                expandedItems: invoices.filter(
                  (i) => i.id.includes('-'), // child IDs include hyphen
                ),
                onExpandableItemToggle: ({ detail }) => {
                  setExpandedInvoiceIds((prev) => {
                    const next = new Set(prev);
                    const id = detail.item.id;

                    if (next.has(id)) {
                      next.delete(id);
                      // Collapse: remove children
                      setInvoices((prevInvoices) => prevInvoices.filter((i) => !i.id.startsWith(`${id}-`)));
                    } else {
                      next.add(id);
                      // Expand: find children from rawInvoices
                      const parent = rawInvoices.find((r) => r.id === id);
                      const children =
                        parent?.items?.map((child) => ({
                          ...child,
                          id: `${parent.id}-${child.id}`,
                          items: undefined,
                        })) ?? [];

                      setInvoices((prev) => {
                        const insertIndex = prev.findIndex((i) => i.id === id);
                        const next = [...prev];
                        next.splice(insertIndex + 1, 0, ...children);
                        return next;
                      });
                    }

                    return next;
                  });
                },
              }}
            />
          )}
        </SpaceBetween>
      </ContentLayout>
      {selectedInvoice && (
        <GridLinkModal
          showModal={showModal}
          setShowModal={setShowModal}
          fromId={selectedInvoice}
          fromType="invoice"
          toTypes={['batch', 'inventory']}
        />
      )}
    </>
  );
};

export default Invoices;
